import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { AppliedFilters } from "../types";

type DashboardToolbarProps = {
  activeView: "men" | "women" | "team";
  onViewChange: (view: "men" | "women" | "team") => void;
  teamDivisionView: "men" | "women";
  onTeamDivisionChange: (view: "men" | "women") => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  filteredCount: number;
  appliedFilters: AppliedFilters;
  roleFilter: string | null;
  statusFilter: string | null;
  categoryFilter: string | null;
  setFilter: string | null;
  uniqueSets: string[];
  filterMenu: boolean;
  setRoleFilter: (value: string | null) => void;
  setStatusFilter: (value: string | null) => void;
  setCategoryFilter: (value: string | null) => void;
  setSetFilter: (value: string | null) => void;
  setAppliedFilters: (
    value: AppliedFilters | ((prev: AppliedFilters) => AppliedFilters),
  ) => void;
  setFilterMenu: (value: boolean | ((prev: boolean) => boolean)) => void;
  onAuctionWindowOpen: () => void;
  onClearQueue: () => void;
};

const chipSx = {
  backgroundColor: "rgba(255,255,255,0.08)",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.26)",
  borderRadius: "6px",
  height: { xs: 32, md: "1.8vw" },
  fontSize: { xs: "0.85rem", md: "0.8vw" },
  backdropFilter: "blur(6px)",
  "& .MuiChip-deleteIcon": {
    marginLeft: "4px",
    color: "#fff",
  },
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.14)",
  },
};

const DashboardToolbar: React.FC<DashboardToolbarProps> = ({
  activeView,
  onViewChange,
  teamDivisionView,
  onTeamDivisionChange,
  searchTerm,
  setSearchTerm,
  filteredCount,
  appliedFilters,
  roleFilter,
  statusFilter,
  categoryFilter,
  setFilter,
  uniqueSets,
  filterMenu,
  setRoleFilter,
  setStatusFilter,
  setCategoryFilter,
  setSetFilter,
  setAppliedFilters,
  setFilterMenu,
  onAuctionWindowOpen,
  onClearQueue,
}) => {
  const appliedFilterCount =
    Object.values(appliedFilters).filter(Boolean).length;
  const categoryOptions =
    activeView === "women"
      ? ["Icon Player", "Under-19 Player", "General Player"]
      : [
          "Icon Player",
          "Senior Player",
          "Emerging Player",
          "Development Player",
        ];
  const chipLabelWithCount = (label: string) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
      <span>{label}</span>
      <Box
        sx={{
          minWidth: 18,
          height: 18,
          px: 0.5,
          borderRadius: "999px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#111",
          background: "rgba(255,255,255,0.85)",
          lineHeight: 1,
        }}
      >
        {filteredCount}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: { xs: "12px", md: "0.6vw" },
        position: "relative",
        zIndex: 3,
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {[
          { key: "men", label: "MEN'S" },
          { key: "women", label: "WOMEN'S" },
          { key: "team", label: "TEAM" },
        ].map((tab) => {
          const isActive = activeView === tab.key;
          return (
            <Box
              key={tab.key}
              onClick={() => onViewChange(tab.key as "men" | "women" | "team")}
              sx={{
                border: "1px solid rgba(255,255,255,0.28)",
                borderRadius: "6px",
                px: { xs: "10px", md: "0.72vw" },
                minHeight: { xs: 34, md: "1.8vw" },
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: { xs: "0.78rem", md: "0.74vw" },
                letterSpacing: "0.03em",
                background: isActive
                  ? "linear-gradient(90deg, #FF8D1E 0%, #FF3E9A 100%)"
                  : "linear-gradient(180deg, rgba(18,20,25,0.9) 0%, rgba(12,12,14,0.9) 100%)",
                color: isActive ? "#111" : "#fff",
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>
      {activeView === "team" && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {[
            { key: "men", label: "MEN'S" },
            { key: "women", label: "WOMEN'S" },
          ].map((tab) => {
            const isActive = teamDivisionView === tab.key;
            return (
              <Box
                key={`team-sub-${tab.key}`}
                onClick={() => onTeamDivisionChange(tab.key as "men" | "women")}
                sx={{
                  border: "1px solid rgba(255,255,255,0.28)",
                  borderRadius: "6px",
                  px: { xs: "10px", md: "0.72vw" },
                  minHeight: { xs: 34, md: "1.8vw" },
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: { xs: "0.78rem", md: "0.74vw" },
                  letterSpacing: "0.03em",
                  background: isActive
                    ? "linear-gradient(90deg, #FF8D1E 0%, #FF3E9A 100%)"
                    : "linear-gradient(180deg, rgba(18,20,25,0.9) 0%, rgba(12,12,14,0.9) 100%)",
                  color: isActive ? "#111" : "#fff",
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            border: "1px solid rgba(255, 137, 41, 0.4)",
            background:
              "linear-gradient(90deg, rgba(54,24,8,0.95) 0%, rgba(122,54,13,0.9) 100%)",
            py: { xs: 0.55, md: "0.35vw" },
            px: { xs: 1, md: "0.65vw" },
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              bottom: -4,
              height: "4px",
              background:
                "linear-gradient(90deg, #e41f4f 0%, #e41f4f 12%, #24c864 12%, #24c864 26%, #1ba8ff 26%, #1ba8ff 40%, #4e5dff 40%, #4e5dff 54%, #ff8b1a 54%, #ff8b1a 67%, #ff3e4d 67%, #ff3e4d 79%, #ffd400 79%, #ffd400 90%, #22d37b 90%, #22d37b 100%)",
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.05rem", md: "1.6vw" },
              lineHeight: 1,
              background: "linear-gradient(90deg, #FF9A2F 0%, #FF6B39 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Player list t20 Mumbai - 2026
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {appliedFilters.role && (
            <Chip
              label={chipLabelWithCount(appliedFilters.role)}
              onDelete={() => {
                setRoleFilter(null);
                setAppliedFilters((prev) => ({ ...prev, role: undefined }));
              }}
              deleteIcon={<CloseOutlinedIcon />}
              sx={chipSx}
            />
          )}
          {appliedFilters.status && (
            <Chip
              label={chipLabelWithCount(appliedFilters.status)}
              onDelete={() => {
                setStatusFilter(null);
                setAppliedFilters((prev) => ({ ...prev, status: undefined }));
              }}
              deleteIcon={<CloseOutlinedIcon />}
              sx={chipSx}
            />
          )}
          {appliedFilters.category && (
            <Chip
              label={chipLabelWithCount(appliedFilters.category)}
              onDelete={() => {
                setCategoryFilter(null);
                setAppliedFilters((prev) => ({ ...prev, category: undefined }));
              }}
              deleteIcon={<CloseOutlinedIcon />}
              sx={chipSx}
            />
          )}
          {appliedFilters.set && (
            <Chip
              label={chipLabelWithCount(appliedFilters.set)}
              onDelete={() => {
                setSetFilter(null);
                setAppliedFilters((prev) => ({ ...prev, set: undefined }));
              }}
              deleteIcon={<CloseOutlinedIcon />}
              sx={chipSx}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: { xs: "10px", md: "0.8vw" },
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 6 }}>
          <Box
            sx={{
              border: "0.8px solid rgba(255, 255, 255, 0.24)",
              borderRadius: "6px",
              background:
                "linear-gradient(180deg, rgba(18,20,25,0.9) 0%, rgba(12,12,14,0.9) 100%)",
              px: { xs: "12px", md: "0.78vw" },
              minHeight: { xs: 40, md: "2.05vw" },
              display: "flex",
              gap: "6px",
              cursor: "pointer",
              alignItems: "center",
              backdropFilter: "blur(6px)",
              fontSize: { xs: "0.9rem", md: "0.86vw" },
            }}
            onClick={() => setFilterMenu((prev) => !prev)}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span>Filter</span>
              {appliedFilterCount > 0 && (
                <Box
                  sx={{
                    minWidth: { xs: 18, md: "1vw" },
                    height: { xs: 18, md: "1vw" },
                    px: { xs: 0.5, md: "0.2vw" },
                    borderRadius: "999px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: "0.72rem", md: "0.62vw" },
                    fontWeight: 700,
                    color: "#111",
                    background:
                      "linear-gradient(90deg, #FF8D1E 0%, #FF3E9A 100%)",
                    lineHeight: 1,
                  }}
                >
                  {appliedFilterCount}
                </Box>
              )}
              <FilterAltOutlinedIcon fontSize="small" />
            </Box>
          </Box>
          {filterMenu && (
            <ClickAwayListener onClickAway={() => setFilterMenu(false)}>
              <Box
                sx={{
                  background:
                    "linear-gradient(180deg, rgba(17,19,24,0.96) 0%, rgba(10,11,16,0.97) 100%)",
                  borderRadius: "12px",
                  padding: "20px",
                  width: 300,
                  minHeight: "400px",
                  position: "absolute",
                  top: "60px",
                  right: "0",
                  isolation: "isolate",
                  zIndex: 50,
                  border: "1px solid rgba(255,255,255,0.15)",
                  boxShadow: "0 16px 36px rgba(0,0,0,0.45)",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6" color="#fff" mb={2}>
                  Filters
                </Typography>
                <TextField
                  select
                  fullWidth
                  value={roleFilter || ""}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-root": {
                      color: "#fff",
                    },
                  }}
                >
                  <option value="">Select role</option>
                  <option value="Batsman">Batsman</option>
                  <option value="Bowler">Bowler</option>
                  <option value="Wicketkeeper">Wicketkeeper</option>
                  <option value="All - rounder">All - rounder</option>
                </TextField>
                <TextField
                  select
                  fullWidth
                  value={statusFilter || ""}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-root": {
                      color: "#fff",
                    },
                    "& .MuiSelect-icon": {
                      color: "#fff",
                    },
                  }}
                >
                  <option value="">Select status</option>
                  <option value="Sold">Sold</option>
                  <option value="Unsold">Unsold</option>
                  <option value="Pending">Pending</option>
                </TextField>
                <TextField
                  select
                  fullWidth
                  value={categoryFilter || ""}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-root": {
                      color: "#fff",
                    },
                    "& .MuiSelect-icon": {
                      color: "#fff",
                    },
                  }}
                >
                  <option value="">Select Category</option>
                  {categoryOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  value={setFilter || ""}
                  onChange={(e) => setSetFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-root": {
                      color: "#fff",
                    },
                    "& .MuiSelect-icon": {
                      color: "#fff",
                    },
                  }}
                >
                  <option value="">Select Set</option>
                  {Array.isArray(uniqueSets) &&
                    [...uniqueSets]
                      .sort((a, b) => {
                        const numA = parseInt(a.match(/\d+/)?.[0] || "0", 10);
                        const numB = parseInt(b.match(/\d+/)?.[0] || "0", 10);
                        return numA - numB;
                      })
                      .map((set: string) => (
                        <option key={set} value={set}>
                          {set}
                        </option>
                      ))}
                </TextField>

                <Box display="flex" gap={2} mt={"auto"}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setRoleFilter(null);
                      setStatusFilter(null);
                      setCategoryFilter(null);
                      setSetFilter(null);
                      setAppliedFilters({});
                    }}
                    sx={{ color: "#fff", borderColor: "#fff", flex: 1 }}
                  >
                    Reset
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setAppliedFilters({
                        ...(roleFilter && { role: roleFilter }),
                        ...(categoryFilter && { category: categoryFilter }),
                        ...(statusFilter && { status: statusFilter }),
                        ...(setFilter && { set: setFilter }),
                      });
                      setFilterMenu(false);
                    }}
                    sx={{
                      bgcolor: "#fff",
                      color: "#0A0C10",
                      fontWeight: 600,
                      flex: 1,
                    }}
                  >
                    Show results
                  </Button>
                </Box>
              </Box>
            </ClickAwayListener>
          )}
        </Box>
        <TextField
          placeholder="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 0.4 }}>
                <SearchIcon style={{ color: "#94969C", fontSize: "16px" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            borderRadius: "6px",
            marginBottom: "0",
            width: { xs: "100%", md: "240px" },
            color: "white",
            "& .MuiOutlinedInput-root": {
              background:
                "linear-gradient(180deg, rgba(6,8,13,0.9) 0%, rgba(8,9,12,0.9) 100%)",
              minHeight: { xs: 40, md: "2.05vw" },
              "& .MuiOutlinedInput-input": {
                py: 0,
                px: 0.6,
              },
              "&:hover fieldset": {
                borderColor: "#fff",
                color: "#fff",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#7F56D9",
              },
              "& input": {
                color: "#fff",
                fontSize: { xs: "0.9rem", md: "0.86vw" },
              },
            },
          }}
        />
        <Box
          sx={{
            border: "0.8px solid rgba(255, 146, 46, 0.44)",
            borderRadius: "6px",
            background: "linear-gradient(90deg, #FF8D1E 0%, #FF3E9A 100%)",
            px: { xs: "12px", md: "0.72vw" },
            minHeight: { xs: 40, md: "2.05vw" },
            display: "flex",
            alignItems: "center",
            fontWeight: 600,
            fontSize: { xs: "0.85rem", md: "0.82vw" },
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
          onClick={onAuctionWindowOpen}
        >
          Auction window
        </Box>
        <Box
          sx={{
            border: "0.8px solid rgba(255, 146, 46, 0.44)",
            borderRadius: "6px",
            background: "linear-gradient(90deg, #FF8D1E 0%, #FF3E9A 100%)",
            px: { xs: "14px", md: "0.95vw" },
            minHeight: { xs: 40, md: "2.05vw" },
            display: "flex",
            gap: "4px",
            alignItems: "center",
            fontWeight: 600,
            fontSize: { xs: "0.9rem", md: "0.86vw" },
            cursor: "pointer",
          }}
          onClick={onClearQueue}
        >
          Clear queue
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardToolbar;
